import { FormEvent, useState } from "react";
import { Eye, EyeOff, Lock, LogIn, Loader2, ShieldCheck } from "lucide-react";
import { ApiError, login, registrieren, twoFactorLogin } from "../api";

type Step = "password" | "totp";

/** Passwort + 2FA in einer Karte.
 *
 *  Bewusst ohne Wissen darüber, wo es steckt: dieselbe Karte trägt die
 *  Login-Seite und den Anmelde-Dialog oben rechts. Wohin es nach dem Erfolg
 *  geht, entscheidet `onDone` — die Seite navigiert, der Dialog schließt sich
 *  nur und lässt den Besucher da, wo er war. */
export function LoginForm({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState<Step>("password");
  /** Der Benutzername. Leer bleibt erlaubt — dann ist der Verwalter
   *  gemeint, wie früher, als es nur ihn gab. */
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  /** „Konto anlegen" statt anmelden. Wer das tut, wartet danach auf die
   *  Freigabe durch den Verwalter. */
  const [neuesKonto, setNeuesKonto] = useState(false);
  const [gemeldet, setGemeldet] = useState(false);
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [shake, setShake] = useState(false);

  function fail(msg: string) {
    setErr(msg);
    setShake(true);
    setTimeout(() => setShake(false), 480);
  }

  async function onPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    if (!pw) return;
    setErr(null);
    setBusy(true);
    try {
      if (neuesKonto) {
        await registrieren(name.trim(), pw);
        setGemeldet(true);
        setPw("");
        return;
      }
      const r = await login(pw, name.trim());
      if ("step" in r && r.step === "2fa") {
        setChallenge(r.challenge);
        setStep("totp");
        setPw("");
        return;
      }
      onDone();
    } catch (e) {
      const msg = e instanceof ApiError && e.status === 401
        ? "Wrong username or password."
        : e instanceof ApiError && e.status === 403
        ? e.message
        : e instanceof Error ? e.message : "Unknown error.";
      fail(msg);
      setPw("");
    } finally {
      setBusy(false);
    }
  }

  async function onCodeSubmit(e: FormEvent) {
    e.preventDefault();
    if (!challenge || !code) return;
    setErr(null);
    setBusy(true);
    try {
      await twoFactorLogin(challenge, code.trim());
      onDone();
    } catch (e) {
      let msg = "Invalid code.";
      if (e instanceof ApiError) {
        if (e.code === "INVALID_CHALLENGE") {
          // Challenge timed out — punt back to password step.
          msg = "Session expired. Please sign in again.";
          setChallenge(null);
          setStep("password");
        }
      } else if (e instanceof Error) msg = e.message;
      fail(msg);
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  function backToPassword() {
    setStep("password");
    setChallenge(null);
    setCode("");
    setErr(null);
  }

  return (
    <form
      className={`login-card${shake ? " shake" : ""}`}
      onSubmit={step === "password" ? onPasswordSubmit : onCodeSubmit}
      aria-busy={busy}
    >
      <div className="login-brand">
        <div className="login-logo">
          {step === "password"
            ? <Lock className="icon-svg" />
            : <ShieldCheck className="icon-svg" />}
        </div>
        <div>
          <h1 className="login-title">home</h1>
          <p className="login-sub">
            {step === "password"
              ? "Sign in to continue"
              : "Code from your authenticator app"}
          </p>
        </div>
      </div>

      {gemeldet ? (
        <p className="login-sub" style={{ marginBottom: "0.8rem" }}>
          Account created. An administrator has to approve it before you can
          sign in.
        </p>
      ) : null}

      {step === "password" ? (
        <label className="login-field">
          <span className="login-label">Username</span>
          <div className="login-input-wrap">
            <input
              type="text"
              placeholder="Username"
              value={name}
              autoFocus
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="username"
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
            />
          </div>
        </label>
      ) : null}

      {step === "password" ? (
        <label className="login-field">
          <span className="login-label">Password</span>
          <div className="login-input-wrap">
            <input
              type={show ? "text" : "password"}
              placeholder="Password"
              value={pw}
              autoComplete={neuesKonto ? "new-password" : "current-password"}
              onChange={(e) => setPw(e.target.value)}
              disabled={busy}
            />
            <button
              type="button"
              className="login-toggle"
              onClick={() => setShow((v) => !v)}
              aria-label={show ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {show ? <EyeOff className="icon-svg" /> : <Eye className="icon-svg" />}
            </button>
          </div>
        </label>
      ) : (
        <label className="login-field">
          <span className="login-label">6-digit code</span>
          <div className="login-input-wrap">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9 \-]*"
              placeholder="123 456"
              value={code}
              autoFocus
              autoComplete="one-time-code"
              onChange={(e) => setCode(e.target.value)}
              disabled={busy}
              className="login-code-input"
            />
          </div>
          <small className="muted login-hint">
            A recovery code works too (e.g. <code>abc12-de345</code>).
          </small>
        </label>
      )}

      {err && <div className="login-error">{err}</div>}

      <button
        type="submit"
        className="login-submit"
        disabled={busy || (step === "password" ? !pw : !code)}
      >
        {busy
          ? <><Loader2 className="icon-svg spin" /> {step === "password" ? "Signing in…" : "Checking…"}</>
          : step === "password"
            ? <><LogIn className="icon-svg" /> {neuesKonto ? "Create account" : "Sign in"}</>
            : <><ShieldCheck className="icon-svg" /> Confirm</>}
      </button>

      {/* Ein zweiter Weg hinein, für den es noch kein Konto gibt. Der
          Verwalter gibt es danach frei — vorher geht hier nichts. */}
      {step === "password" && (
        <button
          type="button"
          className="login-back"
          disabled={busy}
          onClick={() => { setNeuesKonto((v) => !v); setErr(null); setGemeldet(false); }}
        >
          {neuesKonto ? "I already have an account" : "Create an account"}
        </button>
      )}

      {step === "totp" && (
        <button
          type="button"
          onClick={backToPassword}
          className="login-back"
          disabled={busy}
        >
          ← Back
        </button>
      )}

      <p className="login-foot muted">
        Private area — password required{step === "totp" ? " and 2FA code" : ""}.
      </p>
    </form>
  );
}
